import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Alert, Empty, Input, Spin, message } from "antd";
import type { StoreApprovalItem, StoreApprovalStatus } from "./types";
import StoreApprovalTabs from "./components/StoreApprovalTabs";
import StoreApprovalCard from "./components/StoreApprovalCard";
import StoreReviewDrawer from "./components/StoreReviewDrawer";
import StoreActionConfirmModal from "./components/StoreActionConfirmModal";
import type { StoreActionType } from "./components/StoreActionConfirmModal";
import CmsTopBar from "components/cmslayout/CmsTopBar";
import { SearchOutlined } from "@ant-design/icons";
import martEmptyImg from "assets/images/mart-empty.png";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  approveStore,
  clearAdminStoreError,
  clearSelectedStoreKyc,
  declineStore,
  fetchStoresByApprovalStatus,
  fetchVendorKyc,
} from "store/slices/adminStoreSlice";
import type { AdminStore } from "types/adminStore";
import {
  isApprovedStore,
  mapAdminStoreToApprovalItem,
} from "./storeMappers";

interface StoreApprovalPageProps {}

export const StoreApprovalPage: React.FunctionComponent<
  StoreApprovalPageProps
> = () => {
  const dispatch = useAppDispatch();
  const {
    actionLoading,
    actionStoreId,
    actionType,
    error,
    kycError,
    kycLoading,
    loading,
    selectedStoreKyc,
    storesByApprovalStatus,
  } = useAppSelector((state) => state.adminStores);
  const [active, setActive] =
    useState<StoreApprovalStatus>("awaiting_approval");
  const [selectedStore, setSelectedStore] = useState<AdminStore | null>(
    null
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] =
    useState<StoreActionType>("approve");
  const [confirmItem, setConfirmItem] = useState<StoreApprovalItem | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    dispatch(fetchStoresByApprovalStatus("awaiting_approval"));
    dispatch(fetchStoresByApprovalStatus("approved"));
  }, [dispatch]);

  const currentStores = useMemo(
    () => storesByApprovalStatus[active] || [],
    [active, storesByApprovalStatus]
  );

  const counts = useMemo(
    () => ({
      pending: storesByApprovalStatus.awaiting_approval?.length || 0,
      approved: storesByApprovalStatus.approved?.length || 0,
      total:
        (storesByApprovalStatus.awaiting_approval?.length || 0) +
        (storesByApprovalStatus.approved?.length || 0),
    }),
    [storesByApprovalStatus]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const mapped = currentStores.map((store) => ({
      store,
      item: mapAdminStoreToApprovalItem(store),
    }));

    if (!q) return mapped;

    return mapped.filter(({ item }) =>
        item.storeName.toLowerCase().includes(q) ||
        item.ownerName.toLowerCase().includes(q) ||
        item.location.toLowerCase().includes(q)
    );
  }, [currentStores, search]);

  const selectedStoreItem = useMemo(
    () => (selectedStore ? mapAdminStoreToApprovalItem(selectedStore) : null),
    [selectedStore]
  );

  const handleTabChange = (status: StoreApprovalStatus) => {
    setActive(status);
    setSearch("");
    dispatch(clearAdminStoreError());
    dispatch(fetchStoresByApprovalStatus(status));
  };

  const requestConfirm = (action: StoreActionType, item: StoreApprovalItem) => {
    setConfirmAction(action);
    setConfirmItem(item);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (actionLoading) return;
    setConfirmOpen(false);
    setConfirmItem(null);
  };

  const refreshStoreLists = () => {
    dispatch(fetchStoresByApprovalStatus(active));
    if (active !== "awaiting_approval") {
      dispatch(fetchStoresByApprovalStatus("awaiting_approval"));
    }
    if (active !== "approved") {
      dispatch(fetchStoresByApprovalStatus("approved"));
    }
  };

  const confirmNow = async () => {
    if (!confirmItem) return;

    const storeId = Number(confirmItem.id);
    const result =
      confirmAction === "approve"
        ? await dispatch(approveStore(storeId))
        : await dispatch(declineStore(storeId));

    if (
      (confirmAction === "approve" && approveStore.fulfilled.match(result)) ||
      (confirmAction === "decline" && declineStore.fulfilled.match(result))
    ) {
      message.success(
        `Store (${confirmItem.storeName}) ${
          confirmAction === "approve" ? "approved" : "declined"
        }.`
      );
      setSelectedStore(null);
      dispatch(clearSelectedStoreKyc());
      refreshStoreLists();
      setConfirmOpen(false);
      setConfirmItem(null);
      return;
    }

    message.error(result.payload || "Unable to update store.");
  };

  const isActionFor = (item: StoreApprovalItem, type: StoreActionType) =>
    actionLoading && actionStoreId === Number(item.id) && actionType === type;

  const openReview = (store: AdminStore) => {
    setSelectedStore(store);
    dispatch(clearSelectedStoreKyc());

    if (store.vendor_id) {
      dispatch(fetchVendorKyc(store.vendor_id));
    }
  };

  const closeReview = () => {
    setSelectedStore(null);
    dispatch(clearSelectedStoreKyc());
  };

  return (
    <React.Fragment>
      <CmsTopBar title="Store" />

      <Container fluid className="dashboard-wrap">
        <Container fluid className="px-3 py-4">
          <div className="store-approval-card">
            <div className="store-approval-card-header">
              <div className="store-approval-title">
                Store Approvals{" "}
                <span className="store-approval-count">({counts.total})</span>
              </div>
            </div>

            <StoreApprovalTabs
              active={active}
              pendingCount={counts.pending}
              approvedCount={counts.approved}
              onChange={handleTabChange}
            />

            <Row className="mb-3">
              <Col span={24}>
                <Input
                  placeholder="Search"
                  prefix={<SearchOutlined className="markets-search-icon" />}
                  className="markets-search markets-search-full"
                  allowClear
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </Col>
            </Row>

            {error && (
              <Alert
                type="error"
                message={error}
                showIcon
                closable
                className="mb-3"
                onClose={() => dispatch(clearAdminStoreError())}
              />
            )}

            <div className="store-approval-body">
              {loading ? (
                <div className="store-approval-loading">
                  <Spin size="large" />
                </div>
              ) : filtered.length === 0 ? (
                <Empty
                  image={
                    <div
                      style={{
                        height: 130,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={martEmptyImg}
                        alt="No stores"
                        style={{
                          width: 120,
                          height: 120,
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    </div>
                  }
                  description={
                    <div style={{ textAlign: "center", marginTop: 25 }}>
                      <div
                        style={{
                          fontSize: 20,
                          fontWeight: 600,
                          color: "#1f1f1f",
                        }}
                      >
                        {active === "awaiting_approval"
                          ? "No Stores Yet"
                          : "No Approved Stores Yet"}
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          color: "#8c8c8c",
                          marginTop: 4,
                        }}
                      >
                        {active === "awaiting_approval"
                          ? "No stores have been submitted for approval."
                          : "No stores have been approved yet."}
                      </div>
                    </div>
                  }
                />
              ) : (
                <div className="store-approval-grid">
                  {filtered.map(({ store, item }) => (
                    <StoreApprovalCard
                      key={item.id}
                      item={item}
                      onApprove={(store) => requestConfirm("approve", store)}
                      onDecline={(store) => requestConfirm("decline", store)}
                      onReview={() => openReview(store)}
                      approveLoading={isActionFor(item, "approve")}
                      declineLoading={isActionFor(item, "decline")}
                      actionsDisabled={actionLoading}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </Container>
      </Container>

      <StoreReviewDrawer
        open={!!selectedStore}
        item={selectedStoreItem}
        store={selectedStore}
        kyc={selectedStoreKyc}
        kycLoading={kycLoading}
        kycError={
          selectedStore && !selectedStore.vendor_id
            ? "Vendor id is missing. Cannot load KYC."
            : kycError
        }
        onClose={closeReview}
        onApprove={(store) => requestConfirm("approve", store)}
        onDecline={(store) => requestConfirm("decline", store)}
        hideActions={isApprovedStore(selectedStore)}
        approveLoading={
          !!selectedStoreItem && isActionFor(selectedStoreItem, "approve")
        }
        declineLoading={
          !!selectedStoreItem && isActionFor(selectedStoreItem, "decline")
        }
        actionsDisabled={actionLoading}
      />

      <StoreActionConfirmModal
        open={confirmOpen}
        action={confirmAction}
        storeName={confirmItem?.storeName}
        loading={actionLoading}
        onClose={closeConfirm}
        onConfirm={confirmNow}
      />
    </React.Fragment>
  );
};

export default StoreApprovalPage;
